import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

interface Row {
  keys: string[];
  label: string;
}

const groups: { heading: string; rows: Row[] }[] = [
  {
    heading: "Navigation",
    rows: [
      { keys: ["⌘", "K"], label: "Open quick-jump palette" },
      { keys: ["⌥", "1"], label: "Principal Overview" },
      { keys: ["⌥", "2"], label: "Student Profile" },
      { keys: ["⌥", "3"], label: "Parent Dashboard" },
      { keys: ["⌥", "4"], label: "Counsellor Dashboard" },
      { keys: ["⌥", "5"], label: "Learning Studios" },
      { keys: ["⌥", "6"], label: "Portfolio" },
    ],
  },
  {
    heading: "Presenting",
    rows: [
      { keys: ["Shift", "P"], label: "Toggle Presenter Mode" },
      { keys: ["Esc"], label: "Exit Presenter Mode" },
      { keys: ["⌘", "P"], label: "Print current screen" },
      { keys: ["N"], label: "Toggle speaker notes" },
      { keys: ["?"], label: "Show this cheat sheet" },
    ],
  },
  {
    heading: "Autoplay",
    rows: [
      { keys: ["A"], label: "Play / pause auto-advance" },
      { keys: ["]"], label: "Skip to next step" },
      { keys: ["["], label: "Back to previous step" },
    ],
  },
];

/**
 * Global "?" cheat sheet — any demo operator can press ? to see every
 * shortcut available in the WeKIT Education Intelligence™ demo. Ignored while typing.
 */
export default function ShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing) return;
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-4 h-4" />
            Demo shortcuts
          </DialogTitle>
          <DialogDescription>
            Press <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">?</kbd> anytime to toggle this sheet.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 pt-1">
          {groups.map((g) => (
            <div key={g.heading}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                {g.heading}
              </div>
              <ul className="grid gap-1.5">
                {g.rows.map((r) => (
                  <li key={r.label} className="flex items-center justify-between text-sm">
                    <span>{r.label}</span>
                    <span className="flex items-center gap-1">
                      {r.keys.map((k, i) => (
                        <kbd
                          key={i}
                          className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium min-w-[1.5rem] text-center"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
